<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      employees: [],
      search: '',
      statusFilter: '',
      statusOptions: ['active', 'inactive', 'resigned', 'terminated'],
      typeLabels: {
        full_time: 'Full-time',
        part_time: 'Part-time',
        contract: 'Contract',
        intern: 'Intern',
      },
      avatarColors: [
        '#6366f1', '#0ea5e9', '#10b981', '#f59e0b',
        '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6',
      ],
      pagination: {
        current_page: 1,
        per_page: 50,
        last_page: 1,
        total: 0,
        links: [],
      },
      deleteItem: null,
      failedImageIds: {},
    };
  },
  mounted() {
    this.fetchEmployees();
  },
  methods: {
    async fetchEmployees(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/employees', {
          params: {
            page,
            search: this.search || undefined,
            status: this.statusFilter || undefined,
          },
        });
        this.employees = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.fetchEmployees(1);
    },

    clearFilters() {
      this.search = '';
      this.statusFilter = '';
      this.fetchEmployees(1);
    },

    typeLabel(t) {
      return this.typeLabels[t] || t || '-';
    },

    statusClass(status) {
      const map = {
        active: 'emp-st emp-active',
        inactive: 'emp-st emp-inactive',
        resigned: 'emp-st emp-resigned',
        terminated: 'emp-st emp-terminated',
      };
      return map[status] || 'emp-st emp-inactive';
    },

    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    avatarColor(id) {
      const idx = (parseInt(id) || 0) % this.avatarColors.length;
      return this.avatarColors[idx];
    },

    // pagination helpers
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.fetchEmployees(page);
    },

    goAdd() {
      this.$router.push('/hrms/employees/add');
    },
    goEdit(row) {
      this.$router.push(`/hrms/employees/${row.id}/edit`);
    },

    async changeStatus(row, status) {
      if (row.status === status) return;
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/employees/${row.id}`, { status });
        this.$swalToast(res.data.message);
        this.fetchEmployees(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async toggleWfh(row) {
      const newValue = !row.allow_remote;
      try {
        const res = await axios.patch(`/hrms/employees/${row.id}/allow-remote`, {
          allow_remote: newValue,
        });
        row.allow_remote = newValue;
        this.$swalToast(res.data.message);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },

    confirmDelete(row) {
      this.deleteItem = row;
      this.$openModal('hrm_employee_delete_modal');
    },

    async deleteEmployee() {
      this.setLoading(true);
      try {
        const res = await axios.delete(`/hrms/employees/${this.deleteItem.id}`);
        this.$swalToast(res.data.message);
        this.$closeModal('hrm_employee_delete_modal');
        this.fetchEmployees(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
    handleImageError(emp) {
    this.failedImageIds[emp.id] = true;
  },
  imageFailed(emp) {
    return !!this.failedImageIds[emp.id];
  },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Employees</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="goAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Employee
          </a>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
        <div class="card-body d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Employee List
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <select class="form-select form-select-sm" style="width:auto; min-width:130px;" v-model="statusFilter" @change="onSearch">
              <option value="">All Status</option>
              <option v-for="s in statusOptions" :key="s" :value="s">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</option>
            </select>

            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search employees..." @keyup.enter="onSearch" />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>

            <button
              v-if="search || statusFilter"
              type="button"
              class="btn btn-sm btn-light d-flex align-items-center"
              style="height:34px;"
              @click="clearFilters"
              title="Clear filters"
            >
              <i class="ti ti-x me-1"></i>Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="emp-state">
        <div class="spinner-border text-primary mb-2"></div>
        <div>Loading employees...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!employees.length" class="emp-state">
        <i class="ti ti-users d-block mb-2" style="font-size:38px; opacity:.35;"></i>
        No employees found.
      </div>

      <!-- Card Grid -->
      <div v-else class="row g-3 pb-4">
        <div v-for="emp in employees" :key="emp.id" class="col-xxl-3 col-lg-4 col-md-6">
          <div class="emp-card">

            <!-- top: actions -->
            <div class="emp-card-top">
              <div class="d-flex align-items-center gap-2">
                <span :class="statusClass(emp.status)">{{ emp.status }}</span>
                <label class="wfh-toggle" :title="emp.allow_remote ? 'WFH Allowed' : 'WFH Not Allowed'" @click.stop>
                  <input type="checkbox" :checked="emp.allow_remote" @change="toggleWfh(emp)" />
                  <span class="wfh-toggle-slider"></span>
                  <span class="wfh-toggle-label">WFH</span>
                </label>
              </div>
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="goEdit(emp)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li class="dropdown-header" style="font-size:10px; text-transform:uppercase; color:#94a3b8;">Change Status</li>
                  <li v-for="s in statusOptions" :key="s">
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="changeStatus(emp, s)">
                      <i class="ti ti-point me-1" :class="emp.status === s ? 'text-primary' : 'text-muted'"></i>
                      {{ s.charAt(0).toUpperCase() + s.slice(1) }}
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(emp)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!-- avatar + name -->
            <div class="emp-card-head" @click="goEdit(emp)">
              <div class="emp-avatar" :style="{ background: avatarColor(emp.id) }">
                <img
                  v-if="emp.user?.image && !imageFailed(emp)"
                  :src="$profileImage(emp.user.image, emp)"
                  :alt="emp.full_name"
                  @error="handleImageError(emp)"
                />
                <span v-else>{{ initials(emp.full_name) }}</span>
              </div>
              <div class="emp-name-wrap">
                <span class="emp-name">{{ emp.full_name }}</span>
                <span class="emp-code">{{ emp.code }}</span>
              </div>
            </div>

            <!-- meta -->
            <div class="emp-meta">
              <div class="emp-meta-row">
                <i class="ti ti-briefcase"></i>
                <span>{{ emp.position?.name || emp.department?.name || 'No designation' }}</span>
              </div>
              <div class="emp-meta-row">
                <i class="ti ti-building"></i>
                <span>{{ emp.department?.name || '-' }}</span>
              </div>
              <div class="emp-meta-row">
                <i class="ti ti-mail"></i>
                <span class="text-truncate">{{ emp.email }}</span>
              </div>
              <div class="emp-meta-row">
                <i class="ti ti-phone"></i>
                <span>{{ emp.phone || '-' }}</span>
              </div>
            </div>

            <!-- footer -->
            <div class="emp-card-foot">
              <span class="emp-type">{{ typeLabel(emp.employment_type) }}</span>
              <span class="emp-joined"><i class="ti ti-calendar me-1"></i>{{ $formatDate(emp.joining_date) }}</span>
            </div>

          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && employees.length && pagination.last_page > 1" class="d-flex justify-content-center align-items-center gap-2 mt-4">
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === 1" @click="goToPage(pagination.current_page - 1)">
          <i class="ti ti-chevron-left"></i>
        </button>
        <span style="font-size:12.5px; color:#64748b;">
          Page {{ pagination.current_page }} of {{ pagination.last_page }}
        </span>
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === pagination.last_page" @click="goToPage(pagination.current_page + 1)">
          <i class="ti ti-chevron-right"></i>
        </button>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_employee_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">Delete <strong>{{ deleteItem?.full_name }}</strong>? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteEmployee">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-search-wrap {
  display: flex; align-items: center;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  overflow: hidden; background: #fff;
}
.l-search-wrap:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.1); }
.l-search-input {
  border: none; outline: none; height: 34px; padding: 0 10px;
  font-size: 12.5px; color: #1e293b; width: 200px; background: transparent;
}

/* states */
.emp-state {
  text-align: center; padding: 60px 20px;
  color: #94a3b8; font-size: 13.5px;
  background: #fff; border-radius: 14px;
}

/* card */
.emp-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
  height: 100%;
  transition: all .18s ease;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.emp-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 8px 20px rgba(99,102,241,.1);
}

.emp-card-top {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}

.emp-card-head {
  display: flex; align-items: center; gap: 12px;
  cursor: pointer; margin-bottom: 14px;
}
.emp-avatar {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 16px;
  flex-shrink: 0; overflow: hidden;
}
.emp-avatar img { width: 100%; height: 100%; object-fit: cover; }
.emp-name-wrap { display: flex; flex-direction: column; min-width: 0; }
.emp-name {
  font-size: 14px; font-weight: 700; color: #1e293b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.emp-code {
  font-size: 10px; font-weight: 700; letter-spacing: .4px;
  color: #94a3b8; margin-top: 1px;
}

.emp-meta {
  display: flex; flex-direction: column; gap: 7px;
  padding: 12px 0; border-top: 1px solid #f1f5f9;
}
.emp-meta-row {
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: #475569; min-width: 0;
}
.emp-meta-row i { font-size: 14px; color: #94a3b8; flex-shrink: 0; }
.emp-meta-row span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.emp-card-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 12px; border-top: 1px solid #f1f5f9;
}
.emp-type {
  font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 20px;
  background: #eef2ff; color: #4f46e5;
}
.emp-joined { font-size: 11px; color: #94a3b8; }

.emp-st {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px; text-transform: capitalize;
}
.emp-active { background: #dcfce7; color: #16a34a; }
.emp-inactive { background: #f1f5f9; color: #94a3b8; }
.emp-resigned { background: #fef3c7; color: #b45309; }
.emp-terminated { background: #fee2e2; color: #dc2626; }

/* WFH toggle switch */
.wfh-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}
.wfh-toggle input {
  display: none;
}
.wfh-toggle-slider {
  position: relative;
  width: 28px;
  height: 16px;
  background: #e2e8f0;
  border-radius: 20px;
  transition: background .2s ease;
  flex-shrink: 0;
}
.wfh-toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  transition: transform .2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,.15);
}
.wfh-toggle input:checked + .wfh-toggle-slider {
  background: #6366f1;
}
.wfh-toggle input:checked + .wfh-toggle-slider::before {
  transform: translateX(12px);
}
.wfh-toggle-label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: .3px;
  color: #94a3b8;
  text-transform: uppercase;
}
.wfh-toggle input:checked ~ .wfh-toggle-label {
  color: #4f46e5;
}
</style>